import {
  Box,
  Collapsible,
  Flex,
  HStack,
  Switch,
  Text,
  VStack
} from "@chakra-ui/react"
import { DarkMode } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import SliderSwitch from "@pathwright/ux/src/components/slider/SliderSwitch"
import { useMemo, useState } from "react"
import { useResourceQuery } from "@pathwright/graphql-client/src/generated"
import useBlocksMigrationCFPs, {
  SCHOOL_CONTEXT
} from "../blocks/useBlocksMigrationCFPs"
import Pathicon from "../pathicon/Pathicon"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { RESOURCE_RE, getUrlValues } from "../utils/urls"

const LEVEL_LABELS: [string, string, string] = ["Off", "New steps", "All steps"]

/** Inline 3-dot indicator: level 0/1/2 = first/second/third dot active. */
function LevelDots({
  level,
  "aria-label": ariaLabel
}: {
  level: 0 | 1 | 2
  "aria-label": string
}) {
  return (
    <HStack gap={0.5} role="img" aria-label={ariaLabel}>
      {([0, 1, 2] as const).map((i) => (
        <Box
          key={i}
          w="1.5"
          h="1.5"
          borderRadius="full"
          bg={i === level ? "whiteAlpha.600" : "whiteAlpha.300"}
          transitionProperty="background"
          transitionDuration="fast"
        />
      ))}
    </HStack>
  )
}

const rowStyles = {
  minH: "2.8em",
  px: 2,
  py: 1,
  fontSize: ".8rem",
  color: "whiteAlpha.500",
  fontWeight: "semibold",
  borderRadius: "lg",
  alignItems: "center"
}

function BlocksMigrationRow({
  label,
  value,
  disabled,
  onChange,
  onChangeEnd
}: {
  label: string
  value: 0 | 1 | 2
  disabled: boolean
  onChange: (v: 0 | 1 | 2) => void
  onChangeEnd: (v: 0 | 1 | 2) => void
}) {
  return (
    <Box as="div" {...rowStyles}>
      <VStack w="100%" alignItems="stretch" gap={2}>
        <HStack flex="1" minW={0}>
          <Pathicon icon="block" />
          <Text as="span" lineClamp={1} color="whiteAlpha.700">
            {label}
          </Text>
        </HStack>
        <DarkMode>
          <SliderSwitch
            value={value}
            stepCount={3}
            labels={LEVEL_LABELS}
            disabled={disabled}
            onChange={(stepIndex) => onChange(stepIndex as 0 | 1 | 2)}
            onChangeEnd={(stepIndex) => onChangeEnd(stepIndex as 0 | 1 | 2)}
          />
        </DarkMode>
      </VStack>
    </Box>
  )
}

export default function PWAdminCmdKBlocks() {
  const { me, school } = usePathwrightContext()
  const hasStaffAccess = !!me?.permissions?.has_pathwright_staff_access
  const [locked, setLocked] = useState(true)

  const urlValues = getUrlValues(RESOURCE_RE)
  const resourceId = urlValues?.resourceId as number | undefined
  const resourceContext = useMemo(
    () =>
      resourceId != null
        ? { resource_id: resourceId, cohort_id: null, user_id: null }
        : null,
    [resourceId]
  )

  const schoolToggles = useBlocksMigrationCFPs(SCHOOL_CONTEXT, {
    skip: !hasStaffAccess
  })
  const resourceToggles = useBlocksMigrationCFPs(resourceContext, {
    skip: !hasStaffAccess
  })

  const resourceQuery = useResourceQuery({
    variables: { id: resourceId! },
    skip: !resourceId || !hasStaffAccess
  })
  const resourceName =
    resourceQuery.data?.resource?.name ??
    (resourceId ? `Resource ${resourceId}` : "")

  const schoolName = school?.name ?? "this school"

  const sliderDisabled = (loading: boolean) => loading || locked

  if (!hasStaffAccess) {
    return null
  }

  return (
    <VStack w="100%" alignItems="stretch" gap="3px" pt="10px">
      <Flex px={2} pt={2} pb={1} align="center" justify="space-between" gap={2}>
        <HStack flex={1} minW={0} gap={2} align="center">
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="whiteAlpha.600"
            textTransform="uppercase"
            letterSpacing="wider"
          >
            Blocks migration
          </Text>
          {locked && (
            <HStack gap={2} align="center" flexShrink={0}>
              <LevelDots
                level={schoolToggles.level}
                aria-label={`School: ${LEVEL_LABELS[schoolToggles.level]}`}
              />
              {resourceId != null && (
                <LevelDots
                  level={resourceToggles.level}
                  aria-label={`Resource: ${
                    LEVEL_LABELS[resourceToggles.level]
                  }`}
                />
              )}
            </HStack>
          )}
        </HStack>
        <HStack
          gap={1}
          align="center"
          role="group"
          aria-label="Lock changes"
          flexShrink={0}
        >
          <Pathicon
            icon="lock"
            boxSize={3}
            color={locked ? "whiteAlpha.800" : "whiteAlpha.400"}
            aria-hidden
          />
          <DarkMode>
            <Switch.Root
              size="sm"
              checked={!locked}
              onCheckedChange={(e) => setLocked(!e.checked)}
              gap={0}
              aria-label={
                locked ? "Unlock to change" : "Lock to prevent changes"
              }
            >
              <Switch.HiddenInput />
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Switch.Label />
            </Switch.Root>
          </DarkMode>
          <Pathicon
            icon="unlock"
            boxSize={3}
            color={!locked ? "whiteAlpha.800" : "whiteAlpha.400"}
            aria-hidden
          />
        </HStack>
      </Flex>

      <Collapsible.Root open={!locked}>
        <Collapsible.Content>
          <VStack w="100%" alignItems="stretch" gap="3px">
            <BlocksMigrationRow
              label={`Blocks migration for ${schoolName}`}
              value={schoolToggles.level}
              disabled={sliderDisabled(schoolToggles.loading)}
              onChange={(v) => schoolToggles.setLevel(v, { persist: false })}
              onChangeEnd={(v) => schoolToggles.setLevel(v)}
            />
            {resourceId != null && (
              <BlocksMigrationRow
                label={`Blocks migration for ${resourceName}`}
                value={resourceToggles.level}
                disabled={sliderDisabled(resourceToggles.loading)}
                onChange={(v) =>
                  resourceToggles.setLevel(v, { persist: false })
                }
                onChangeEnd={(v) => resourceToggles.setLevel(v)}
              />
            )}
          </VStack>
        </Collapsible.Content>
      </Collapsible.Root>

      {/* <Collapse in={!locked} startingHeight={0} animateOpacity> */}
    </VStack>
  )
}
